import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withOktaAuth } from '@okta/okta-react';
import { withRouter } from 'react-router-dom';
import classnames from 'classnames';
import ReactRouterPropTypes from 'react-router-prop-types';

import { Badge, Icon, Link, GamedayLogo, ShoppingCartIcon } from '../components/';
import { NavigationMain } from './navigationMain/';

import { alwaysShowCart, getUserAccount, getUserData } from '../user/selectors';
import { toggleNavMenu, closeNavMenu, toggleNavigationMain } from '../navigation/actions';
import { selectIsMenuOpen, selectNavigationOpen } from '../navigation/reducer';
import { toggleIsAdminCart } from '../shop/actions';
import { getSelectedDepartment, isShopping } from '../shop/selectors';
import { isReturnStep } from '../orders/orderReturns/reducer';
import { isStaticPage } from '../static/reducer';

import logout from '../util/logout';

import { selectedDepartmentTypes } from '../types/shop';
import { userAccountTypes, userTypes } from '../types/user';
import { navLinksTypes } from '../types/nav';
import { getIsShoppingEnabled, getIsUSSalesOrg, getSalesOrg } from '../gamedayAccount/selectors';
import { isRouteVisibleForSalesOrg } from '../util/isRouteVisibleForSalesOrg';

export class SlideMenuContainer extends Component {
  logout = () => {
    const { oktaAuth } = this.props;
    logout(oktaAuth);
  };

  renderChildren = () => {
    const { children } = this.props;
    return <div className='viewport'>{children}</div>;
  };

  render() {
    const {
      alwaysShowCart,
      children,
      closeNavMenu,
      isOpen,
      isReturnStep,
      isShopping,
      location,
      navigationOpen,
      navLinks,
      notifications,
      selectedDepartment,
      toggleIsAdminCart,
      toggleNavigationMain,
      toggleNavMenu,
      userData,
      userAccount,
      isStaticPage,
      isUSSalesOrg,
      isShoppingEnabled,
      salesOrg,
      authState
    } = this.props;

    if (!authState?.isAuthenticated) return this.renderChildren();
    const isProfile = /^\/(notifications)|(manage-notification)|(profile)/.test(location.pathname);
    const filteredNavLinksByUserRole = navLinks.filter(navLink =>
      navLink.routes.some(route => (route && !route.limitToRoles) || route.limitToRoles.includes(userData.role))
    );
    const filteredNavLinksByLimitation = filteredNavLinksByUserRole.map(navLink => {
      const filteredRoutes = navLink.routes.filter(route => isRouteVisibleForSalesOrg(route, salesOrg));

      if (!isShoppingEnabled) {
        if (navLink.section === 'global.shop') {
          return {
            ...navLink,
            routes: [],
            disableLink: true
          };
        }
        if (navLink.section === 'global.orders') {
          return {
            ...navLink,
            routes: filteredRoutes.map(route => {
              if (route.path === '/orders/open') {
                return {
                  ...route,
                  disableLink: true
                };
              }
              return route;
            })
          };
        }
      }
      return {
        ...navLink,
        routes: filteredRoutes
      };
    });
    if (!navigationOpen && location.pathname === '/') {
      toggleNavigationMain();
    }

    return (
      <div className={classnames('menu-container', { 'show-menu': isOpen, isProfile })} tabIndex='-1'>
        {navigationOpen && (
          <div
            className={classnames('menu-aside bg-white', {
              'navigation__main-hide': isReturnStep || isStaticPage,
              isUsSalesOrg: isUSSalesOrg
            })}
          >
            <NavigationMain
              alwaysShowCart={alwaysShowCart}
              isOpen={isOpen}
              isShopping={isShopping}
              logout={this.logout}
              navLinks={filteredNavLinksByLimitation}
              selectedDepartment={selectedDepartment}
              toggleIsAdminCart={toggleIsAdminCart}
              toggleNavMenu={toggleNavMenu}
              user={userData}
              userAccount={userAccount}
            />
            {isUSSalesOrg && (
              <div className='tempMANABanner'>
                Orders submitted after June 10th will not ship until after July 8th. Please contact your Nike Service
                Representative with any questions.
              </div>
            )}
          </div>
        )}
        <div className={classnames('viewport', { isUsSalesOrg: isUSSalesOrg })}>
          {navigationOpen && (
            <div className='viewport__header'>
              <button
                type='button'
                className='menu-btn d-lg-h d-sm-b prl5-sm'
                onClick={toggleNavMenu}
                data-desc='slideMenu__button_nav'
              >
                <Badge count={notifications} />
                <Icon name='hamburger' alt='menu' />
              </button>
              <Link to='/' className='d-lg-h d-sm-ib' data-desc='slideMenu__link_home'>
                <GamedayLogo />
              </Link>
              <div className='prl5-sm d-lg-h d-sm-ib'>
                {selectedDepartment && (alwaysShowCart || isShopping) && <ShoppingCartIcon />}
              </div>
            </div>
          )}
          {children}
        </div>
        <button
          type='button'
          className='menu-lightbox'
          aria-label='Close Nav'
          onClick={closeNavMenu}
          data-desc='slideMenu__button_navClose'
        />
      </div>
    );
  }
}

SlideMenuContainer.propTypes = {
  alwaysShowCart: PropTypes.bool.isRequired,
  oktaAuth: PropTypes.shape({
    tokenManager: PropTypes.object
  }).isRequired,
  authState: PropTypes.shape({ isAuthenticated: PropTypes.bool.isRequired }).isRequired,
  children: PropTypes.node.isRequired,
  closeNavMenu: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isShopping: PropTypes.bool.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  navLinks: navLinksTypes.isRequired,
  navigationOpen: PropTypes.bool.isRequired,
  notifications: PropTypes.number,
  selectedDepartment: selectedDepartmentTypes,
  toggleIsAdminCart: PropTypes.func.isRequired,
  toggleNavMenu: PropTypes.func.isRequired,
  toggleNavigationMain: PropTypes.func.isRequired,
  userData: userTypes,
  userAccount: userAccountTypes,
  isReturnStep: PropTypes.bool.isRequired,
  isStaticPage: PropTypes.bool.isRequired,
  isUSSalesOrg: PropTypes.bool.isRequired,
  isShoppingEnabled: PropTypes.bool.isRequired,
  salesOrg: PropTypes.string.isRequired
};

SlideMenuContainer.defaultProps = {
  notifications: 0,
  selectedDepartment: null,
  userData: null,
  userAccount: null
};

const mapStateToProps = (state, ownProps) => ({
  alwaysShowCart: alwaysShowCart(state),
  isOpen: selectIsMenuOpen(state),
  isShopping: isShopping(state),
  navLinks: ownProps.navLinks || [],
  navigationOpen: selectNavigationOpen(state),
  selectedDepartment: getSelectedDepartment(state),
  userData: getUserData(state),
  userAccount: getUserAccount(state),
  isReturnStep: isReturnStep(state),
  isStaticPage: isStaticPage(state),
  isUSSalesOrg: getIsUSSalesOrg(state),
  isShoppingEnabled: getIsShoppingEnabled(state),
  salesOrg: getSalesOrg(state)
});

const mapDispatchToProps = {
  closeNavMenu,
  toggleIsAdminCart,
  toggleNavMenu,
  toggleNavigationMain
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default withOktaAuth(withRouter(connector(SlideMenuContainer)));
